import { BookingRemarksComponent } from '@components/shared/booking-remarks/booking-remarks.component';
import { ErrorStateComponent } from '@components/shared/error-state/error-state.component';
import { ChainMarksComponent } from '@components/shared/chain-marks/chain-marks.component';
import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { GuestInfoComponent } from '@components/shared/guest-info/guest-info.component';
import { FlatInfo } from '@models/flat-info.model';
import { TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, input, Input, output, Output } from '@angular/core';
import { BadgeInfoComponent } from '../../../../../shared/badge-info/badge-info.component';
import { GuestInfoDisplayMode } from '@models/guest-info.model';
import { ConfirmableBookingRow, PresenceStatus } from '../../models/booking-overview.model';
import { createSkeletonRows, ListLoadStatus, SkeletonColumn, SkeletonRowPlaceholder, TableView, tableViewFor } from '../../models/list-load-state';
import { OverviewSkeletonRowComponent } from '../skeleton-row/overview-skeleton-row.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-confirmable-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    BadgeModule,
    GuestInfoComponent,
    FlatInfoComponent,
    BadgeInfoComponent,
    ChainMarksComponent,
    BookingRemarksComponent,
    ErrorStateComponent,
    OverviewSkeletonRowComponent
  ],
  template: `
    <div class="booking-table-container">
      <!-- Load error -->
      @if (view === 'error') {
        <app-error-state
          [title]="'bookingOverview.loadError.title'"
          [message]="'bookingOverview.loadError.message'"
          [retryText]="'common.retry'"
          (onRetry)="retry.emit()"
        />
      }

      <!-- Empty state message -->
      @if (view === 'empty') {
        <div class="p-4 text-center">
          @if (isFilterActive) {
            <p>{{ 'bookingOverview.noFilteredConfirmable' | translate }}</p>
            <p class="text-sm text-gray-500">{{ 'bookingOverview.totalConfirmable' | translate }}: {{ totalCount }}</p>
          } @else {
            <p>{{ 'bookingOverview.noConfirmableBookings' | translate }}</p>
          }
        </div>
      }

      <!-- Table -->
      @if (view === 'rows' || view === 'skeleton') {
        <p-table
          [value]="tableRows"
          styleClass="p-datatable-sm"
          [tableStyle]="{ 'min-width': '60rem' }"
          [scrollable]="true"
          responsiveLayout="scroll"
          [sortField]="sortField"
          [sortOrder]="sortOrder"
          (onSort)="onSort($event)"
        >
          <ng-template pTemplate="header">
            <tr>
              <th
                [pSortableColumn]="'guest.name'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.guest' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'guest.name' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'guest.name' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'guest.name') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'flatInfo'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.flat' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'flatInfo') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle; width: 80px;" class="text-center">
                {{ 'bookingOverview.presence' | translate }}
              </th>
              <th
                [pSortableColumn]="'checkInDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkInDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'checkOutDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkOut' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkOutDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th>{{ 'bookingOverview.status' | translate }}</th>
              <th
                [pSortableColumn]="'isPaid'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.paid' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'isPaid' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'isPaid' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'isPaid') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'isCheckedIn'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkedIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'isCheckedIn') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle">{{ 'common.actions' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template
            pTemplate="body"
            let-booking
          >
            @if (view === 'skeleton') {
              <tr appOverviewSkeletonRow [columns]="skeletonColumns"></tr>
            } @else {
              <tr class="animate-fadein animate-duration-200 animate-ease-out">
                <td>
                  <div>
                    <app-guest-info [guestInfo]="booking.guest" [displayMode]="displayMode">
                      <app-booking-remarks guestNameSuffix [remarks]="booking.remarks" />
                    </app-guest-info>
                    <div class="mt-1 flex flex-wrap gap-1"><app-chain-marks [marks]="booking.chain"></app-chain-marks></div>
                  </div>
                </td>
                <td>
                  @if (booking.flatInfos.length > 0) {
                    @for (flat of booking.flatInfos; track flat; let i = $index) {
                      <app-flat-info [flatInfo]="flat"></app-flat-info>
                      @if (i < booking.flatInfos.length - 1) {
                        <br />
                      }
                    }
                  } @else {
                    {{ 'common.unknown' | translate }}
                  }
                </td>
                <td class="text-center">
                  <!-- Loading state -->
                  @if (booking.presenceLoading) {
                    <i class="pi pi-spin pi-spinner text-sm"></i>
                  }
                
                  <!-- Presence status - Occupied -->
                  @if (!booking.presenceLoading && booking.presenceStatus && booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                        <i class="pi pi-check text-sm text-green-600"></i>
                      </div>
                    </div>
                  }
                
                  <!-- Presence status - Empty -->
                  @if (!booking.presenceLoading && booking.presenceStatus && !booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                        <i class="pi pi-minus text-sm text-gray-400"></i>
                      </div>
                    </div>
                  }
                
                  <!-- No presence data -->
                  @if (!booking.presenceLoading && !booking.presenceStatus) {
                    <span class="text-gray-400 text-sm">—</span>
                  }
                </td>
                <td>{{ formatDate(booking.checkInDate) }}</td>
                <td>{{ formatDate(booking.checkOutDate) }}</td>
                <td>
                  <app-badge-info [badge]="booking.bookingStatus"></app-badge-info>
                </td>
                <td class="text-center">
                  <div class="flex justify-center">
                    @if (booking.isPaid) {
                      <div class="flex items-center justify-center rounded-full bg-green-100 p-1 text-green-700">
                        <i class="pi pi-check !text-sm font-bold !leading-none"></i>
                      </div>
                    } @else {
                      <span class="text-lg text-red-300">—</span>
                    }
                  </div>
                </td>
                <td class="text-center">
                  <div class="flex justify-center">
                    @if (booking.isCheckedIn) {
                      <div class="flex items-center justify-center rounded-full bg-green-100 p-1 text-green-700">
                        <i class="pi pi-check !text-sm font-bold !leading-none"></i>
                      </div>
                    } @else {
                      <span class="text-lg text-red-300">—</span>
                    }
                  </div>
                </td>
                <td>
                  <div class="flex gap-2">
                    <button
                      pButton
                      icon="pi pi-eye"
                      label="{{ 'common.details' | translate }}"
                      class="p-button-outlined p-button-sm rounded-md"
                      (click)="onGuestInfoClick(booking)"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-comments"
                      class="p-button-outlined p-button-sm rounded-md"
                      pTooltip="{{ 'communication.title' | translate }}"
                      tooltipPosition="top"
                      (click)="onCommunicationClick(booking)"
                    ></button>
                  </div>
                </td>
              </tr>
            }
          </ng-template>
        </p-table>
      }
    </div>
  `
})
export class ConfirmableTableComponent {
  @Input() confirmableBookings: ConfirmableBookingRow[] = [];
  @Input() isFilterActive: boolean = false;
  @Input() totalCount: number = 0;
  @Input() displayMode: GuestInfoDisplayMode = GuestInfoDisplayMode.USER;

  readonly loadStatus = input<ListLoadStatus>('ready');
  readonly retry = output<void>();

  protected readonly skeletonRows = createSkeletonRows();
  protected readonly skeletonColumns: readonly SkeletonColumn[] = ['guest', 'flat', 'icon', 'text', 'text', 'text', 'icon', 'icon', 'actions'];

  /** Töltés közben helykitöltő, hibánál hibaállapot, különben a sorok vagy az üres állapot. */
  protected get view(): TableView {
    return tableViewFor(this.loadStatus(), this.confirmableBookings?.length ?? 0);
  }

  /** A p-table sorai: töltés közben a helykitöltők, különben a kapott sorok. */
  protected get tableRows(): (ConfirmableBookingRow | SkeletonRowPlaceholder)[] {
    return this.view === 'skeleton' ? this.skeletonRows : this.confirmableBookings;
  }

  // Action events that the parent component can listen to
  @Output() guestInfoClicked = new EventEmitter<number>();
  @Output() presenceClicked = new EventEmitter<{ booking: ConfirmableBookingRow; presenceStatus: PresenceStatus | undefined }>();
  @Output() communicationClicked = new EventEmitter<ConfirmableBookingRow>();

  private readonly translateService = inject(TranslateService);

  sortField: string = '';
  sortOrder: number = 1;
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: null,
    direction: null
  };

  constructor() {}

  /**
   * Format date to display in table
   * @param date Date object to format
   * @returns Formatted date string
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    try {
      if (typeof date === 'string') {
        return format(new Date(date), 'yyyy-MM-dd');
      }
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return '';
    }
  }

  /**
   * Get severity class for booking status
   * @param statusId Status ID
   * @returns Severity class for PrimeNG tag component
   */
  getStatusSeverity(statusId: number): string {
    switch (statusId) {
      case 1: // Confirmed
        return 'success';
      case 2: // Pending
        return 'warning';
      case 3: // Cancelled
        return 'danger';
      default:
        return 'info';
    }
  }

  /**
   * Handle guest info button click
   * @param booking Booking data
   */
  onGuestInfoClick(booking: ConfirmableBookingRow): void {
    if (booking.id) {
      this.guestInfoClicked.emit(booking.id);
    }
  }

  /**
   * Handle presence tag click
   * @param booking Booking data
   */
  onPresenceClick(booking: ConfirmableBookingRow): void {
    this.presenceClicked.emit({
      booking,
      presenceStatus: booking.presenceStatus
    });
  }

  onCommunicationClick(booking: ConfirmableBookingRow): void {
    this.communicationClicked.emit(booking);
  }

  /**
   * Get tooltip text for presence status
   * @param booking Booking data
   * @returns Tooltip text
   */
  getPresenceTooltip(booking: ConfirmableBookingRow): string {
    if (!booking.presenceStatus) return '';
    
    const status = booking.presenceStatus.isOccupied 
      ? this.translateService.instant('presence.occupied')
      : this.translateService.instant('presence.empty');
    
    return `${status} • ${this.getTimeSince(booking.presenceStatus.minutesSinceChange)}`;
  }

  /**
   * Format minutes since change to human readable text
   * @param minutes Minutes since presence change
   * @returns Formatted time string
   */
  private getTimeSince(minutes: number): string {
    if (minutes < 1) return 'kevesebb mint 1 perce';
    if (minutes < 60) return `${Math.floor(minutes)} perce`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 órája';
    if (hours < 24) return `${hours} órája`;
    const days = Math.floor(hours / 24);
    return `${days} napja`;
  }

  /**
   * Handle sort event from p-table
   * @param event Sort event data
   */
  onSort(event: any): void {
    this.sortField = event.field;
    this.sortOrder = event.order;

    // Update sort state for custom icons
    this.sortState.field = event.field;
    this.sortState.direction = event.order === 1 ? 'asc' : 'desc';

    if (this.sortField === 'flatInfo') {
      this.confirmableBookings = [...this.confirmableBookings].sort((a, b) => {
        const result = FlatInfo.compare(a.flatInfos[0], b.flatInfos[0]);
        return this.sortOrder === 1 ? result : -result;
      });
    }
  }
}
